import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, CreditCard, Ship, ArrowRight, Info } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import api, { inr, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function Checkout() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [session, setSession] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: b } = await api.get(`/bookings/${bookingId}`);
        setBooking(b);
        if (b.status === "PAID") {
          navigate(`/confirmation/${bookingId}`);
          return;
        }
        const { data: s } = await api.post("/payments/create-session", { booking_id: bookingId });
        setSession(s);
      } catch (e) {
        toast.error(apiError(e.response?.data?.detail));
      }
    })();
  }, [bookingId, navigate]);

  const pay = async (outcome) => {
    if (!session) return;
    setProcessing(true);
    try {
      const { data } = await api.post("/payments/mock-pay", { order_id: session.order_id, outcome });
      if (data.status === "PAID") {
        toast.success("Payment successful!");
        navigate(`/confirmation/${bookingId}`);
      } else {
        toast.error("Payment failed. Please try again.");
        setProcessing(false);
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setProcessing(false);
    }
  };

  if (!booking) return <div className="min-h-screen bg-background"><Navbar /></div>;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8 grid gap-6 lg:grid-cols-[1fr_320px]">
        <div>
          <p className="overline text-accent mb-1">Secure checkout</p>
          <h1 className="font-display font-bold text-2xl text-primary tracking-tight mb-2">Complete your payment</h1>
          <p className="text-sm text-muted-foreground mb-6">Booking reference <span className="font-mono font-semibold text-primary">{booking.booking_ref}</span></p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex gap-3" data-testid="mock-notice">
            <Info className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <strong>Demo mode:</strong> Cashfree checkout is simulated (no real keys configured). Choose an outcome below to test the full booking flow.
            </div>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 text-primary">
                <CreditCard className="h-4 w-4 text-accent" /> Cashfree Payment Gateway
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-primary text-white">
                <span className="text-sm text-white/80">Amount payable</span>
                <span className="font-display font-bold text-2xl" data-testid="checkout-amount">{inr(booking.amount)}</span>
              </div>
              <Button className="w-full bg-accent hover:bg-accent/90 text-white font-semibold h-12" onClick={() => pay("success")} disabled={processing || !session} data-testid="pay-success-btn">
                {processing ? "Processing payment…" : <><Lock className="h-4 w-4 mr-2" /> Pay {inr(booking.amount)}</>}
              </Button>
              <Button variant="outline" className="w-full text-muted-foreground" onClick={() => pay("failure")} disabled={processing || !session} data-testid="pay-fail-btn">
                Simulate payment failure
              </Button>
              <p className="text-xs text-muted-foreground flex items-center justify-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" /> Payments secured with 256-bit encryption
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="lg:sticky lg:top-24 h-fit">
          <Card data-testid="checkout-summary">
            <CardHeader className="pb-3"><CardTitle className="text-base text-primary">Trip summary</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Ship className="h-3.5 w-3.5 text-accent" /> {booking.ferry_name}
              </div>
              <div className="flex items-center gap-3">
                <div>
                  <div className="font-display font-bold text-primary">{booking.departure_time}</div>
                  <div className="text-xs text-muted-foreground">{booking.from_port?.city}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-accent" />
                <div>
                  <div className="font-display font-bold text-primary">{booking.arrival_time}</div>
                  <div className="text-xs text-muted-foreground">{booking.to_port?.city}</div>
                </div>
              </div>
              <div className="border-t border-border pt-3 flex justify-between">
                <span className="text-muted-foreground capitalize">{booking.travel_class} × {booking.seat_count}</span>
                <span className="font-display font-bold text-primary">{inr(booking.amount)}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
